<script lang="ts">
  import { getAnchorFromCmsLink } from '$lib/utils/cms';
  import { selectedMediaGridTag } from '$lib/stores/selectedMediaGridTag';
  import type { MediaGridItemStoryblok } from '$types/bloks';

  interface MediaGridItemWithSize extends MediaGridItemStoryblok {
    size: 'sm' | 'md' | 'lg' | 'fill';
  }
  import type { MediaGridStoryblok } from '$types/bloks';
  import { cva } from 'class-variance-authority';
  import ResponsiveImage from './marketing-sub-blocks/responsive-image.svelte';
  import ResponsiveVideo from './responsive-video.svelte';
  import { storyblokEditable } from '$lib/actions/storyblok-editable';
  import { createBreakpointMediaQueryStore } from '$lib/stores/media';
  import Skeleton from '$components/ui/skeleton.svelte';
  import { cn } from '$lib/utils';
  import { getUUID } from '$lib/utils/uuid';
  import { getCampaignColors } from '$lib/stores/campaigns';
  import RadioButton from '$components/ui/radio-button.svelte';
  import { onMount } from 'svelte';

  export let block: MediaGridStoryblok;

  const itemsSizeStyle = cva([''], {
    variants: {
      size: {
        sm: 'aspect-[183/196]',
        md: 'aspect-[183/245]',
        lg: 'aspect-[183/269]',
        fill: 'flex-1 aspect-[183/196]'
      }
    }
  });

  const desktop = createBreakpointMediaQueryStore('lg', 'min-width', true);

  $: columnCount = $desktop ? 4 : 2;

  let selectedTag = '';

  $: selectedTag &&
    selectedMediaGridTag.update((map) => {
      const newMap = new Map(map);
      newMap.set(block?._uid || '', selectedTag);
      return newMap;
    });

  $: filteredItems = selectedTag
    ? (block?.items?.filter((item) => {
        const itemTags = item.tags?.split('\n').flat();
        return itemTags?.some((t) => t === selectedTag);
      }) ?? [])
    : (block?.items ?? []);

  $: columns = getMasonryColumns(filteredItems, columnCount);

  $: tags = [
    ...new Set(
      (block?.items?.map((item) => item.tags?.split('\n').flat().filter(Boolean)) ?? []).flat()
    )
  ].map((tagLabel) => {
    return {
      id: getUUID(),
      label: tagLabel || ''
    };
  });

  const getSkeletonItemSize = (index: number): 'sm' | 'md' | 'lg' => {
    const pattern = ['lg', 'sm', 'md', 'lg', 'sm', 'md', 'lg', 'sm'] as const;
    return pattern[index % pattern.length];
  };

  // Creates a masonry-style grid layout by distributing items across columns with varying sizes.
  // Items are distributed like reading text: starting from the first column and filling each column
  // from top to bottom before moving to the next column (e.g. in a 4-column grid: 1,5,9 in first column, 2,6,10 in second, 3,7,11 in third, 4,8,12 in fourth).
  // The last item in each column is set to 'fill' size for better layout control.
  const getMasonryColumns = (
    items: MediaGridStoryblok['items'],
    n_columns: number
  ): MediaGridItemWithSize[][] => {
    if (!items?.length) return [];

    const rows = Math.ceil(items.length / n_columns);

    const columns: MediaGridItemWithSize[][] = Array(n_columns)
      .fill([])
      .map(() => []);

    const pattern = ['lg', 'md', 'sm', 'lg', 'sm', 'lg', 'md', 'sm'];

    for (let col = 0; col < n_columns; col++) {
      const itemsInColumn = Math.ceil((items.length - col) / n_columns);
      for (let row = 0; row < rows; row++) {
        const index = row * n_columns + col;
        if (index < items.length) {
          const item = items[index];
          if (item) {
            const newItem = {
              ...item,
              size: (row === itemsInColumn - 1 ? 'fill' : pattern[index % pattern.length]) as
                | 'sm'
                | 'md'
                | 'lg'
                | 'fill'
            };

            columns[col].push(newItem);
          }
        }
      }
    }

    return columns;
  };

  $: colors = block?.colors ? getCampaignColors(block?.colors) : undefined;
  $: backgroundColor = colors?.altTextColor;
  $: textColor = colors?.altBackgroundColor;

  onMount(() => {
    const tag = $selectedMediaGridTag.get(block?._uid || '');
    if (tag) {
      selectedTag = tag;
    } else {
      selectedTag = block?.default_filter || '';
      selectedMediaGridTag.update((map) => {
        const newMap = new Map(map);
        newMap.set(block?._uid || '', selectedTag);
        return newMap;
      });
    }
  });
</script>

<section
  class="relative px-2 pb-2 lg:pb-8 lg:px-8 h-full mt-4 overflow-hidden"
  use:storyblokEditable={block}
>
  {#if tags && tags.length > 0}
    <div
      class={cn(
        'flex gap-2 hide-scrollbar overflow-x-auto',
        'px-2 mb-4 -mx-2',
        'lg:justify-center lg:mx-0 lg:flex-wrap lg:px-0 '
      )}
    >
      {#each tags as tag}
        <RadioButton
          class="whitespace-nowrap h-fit"
          id={tag.id}
          label={tag.label}
          value={tag.label}
          bind:group={selectedTag}
          selectedBackgroundColor={backgroundColor}
          selectedTextColor={textColor}
        />
      {/each}
    </div>
  {/if}
  {#if block && columns?.length}
    <div class="container isolate mx-auto">
      <div class="flex gap-2 lg:gap-4 container isolate mx-auto">
        {#if $desktop != undefined}
          {#each columns as column}
            <div class="flex flex-col flex-1 gap-2 lg:gap-4">
              {#each column as item}
                {@const link = item.link?.id ? $getAnchorFromCmsLink(item.link) : null}
                <a
                  href={link?.href}
                  rel={link?.rel}
                  target={link?.target}
                  class={cn(
                    'relative rounded-md overflow-hidden inline-block w-full bg-primary/5',
                    itemsSizeStyle({
                      size: item.size
                    })
                  )}
                >
                  {#if item.image && item.image?.length > 0}
                    <ResponsiveImage
                      image={item.image[0]}
                      sizes={{ default: '300px' }}
                      widths={[100, 150, 200, 250]}
                      imgClass="w-full h-full object-cover"
                      class="w-full h-full"
                    />
                  {:else if item.video && item.video?.length > 0}
                    <ResponsiveVideo block={item.video[0]} />
                  {/if}

                  <div class="absolute bottom-0 z-10 flex gap-2 left-0 w-auto p-2">
                    {#if item.cta_label}
                      <p class="flex-1 lg:flex-none">
                        <span
                          class="w-full inline-block text-highlight bg-primary/18 px-3.5 py-3 rounded-xs text-center font-medium lg:font-semibold backdrop-blur text-md"
                          >{item.cta_label}</span
                        >
                      </p>
                    {/if}
                  </div>
                </a>
              {/each}
            </div>
          {/each}
        {:else}
          <div class="w-full columns-2 lg:columns-4 gap-2 lg:gap-4">
            {#each Array(8) as _, i}
              <Skeleton
                class=" rounded-md overflow-hidden w-full mb-2 lg:mb-4 {itemsSizeStyle({
                  size: getSkeletonItemSize(i)
                })}"
              />
            {/each}
          </div>
        {/if}
      </div>
    </div>
  {/if}
</section>
